/**
 * Listener-desired Available now state.
 * Heartbeats follow this so a stale /presence/me or idle tick cannot
 * clear a listing the listener just turned on. sessionStorage keeps the
 * intent across a hard refresh of /dashboard.
 */

const STORAGE_KEY = 'sl-presence-intent';

const desiredOnline = new Set();
const desiredOffline = new Set();
const listeners = new Set();

const persistIntent = () => {
  if (typeof sessionStorage === 'undefined') {
    return;
  }
  try {
    sessionStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ on: [...desiredOnline], off: [...desiredOffline] })
    );
  } catch (e) {
    // Private mode / quota: intent still lives in memory for this tab.
  }
};

const hydrateIntent = () => {
  if (typeof sessionStorage === 'undefined') {
    return;
  }
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return;
    }
    const parsed = JSON.parse(raw);
    (parsed.on || []).forEach(id => desiredOnline.add(id));
    (parsed.off || []).forEach(id => desiredOffline.add(id));
  } catch (e) {
    // Ignore a bad payload.
  }
};

hydrateIntent();

const notifyPresenceIntent = () => {
  listeners.forEach(listener => {
    listener([...desiredOnline]);
  });
};

export const setPresenceIntent = (listingId, available) => {
  if (!listingId) {
    return;
  }
  if (available) {
    desiredOnline.add(listingId);
    desiredOffline.delete(listingId);
  } else {
    desiredOnline.delete(listingId);
    desiredOffline.add(listingId);
  }
  persistIntent();
  notifyPresenceIntent();
};

export const getPresenceIntentIds = () => [...desiredOnline];

export const getPresenceOfflineIntentIds = () => [...desiredOffline];

export const hasPresenceIntent = listingId => desiredOnline.has(listingId);

export const clearPresenceIntent = () => {
  if (desiredOnline.size === 0 && desiredOffline.size === 0) {
    return;
  }
  desiredOnline.clear();
  desiredOffline.clear();
  persistIntent();
  notifyPresenceIntent();
};

export const subscribePresenceIntent = listener => {
  if (typeof listener !== 'function') {
    return () => {};
  }
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export const resetPresenceIntentForTests = () => {
  desiredOnline.clear();
  desiredOffline.clear();
  listeners.clear();
  if (typeof sessionStorage !== 'undefined') {
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch (e) {
      // ignore
    }
  }
};
